import { useState } from "react";
import { Sparkles, Loader2, AlertTriangle, Info, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* "What Am I Forgetting?" — one-tap AI + rules scan of the trip's stored data. */
export default function ForgettingScan({ trip }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/forgetting-scan`);
      setResult(data);
    } catch { toast.error("Scan failed — try again"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={scan} disabled={busy} data-testid="forgetting-scan-btn"
        className="mt-2 ml-2 inline-flex items-center gap-1.5 border border-violet-400 bg-violet-50 text-violet-700 px-3 py-2 text-sm font-700 hover:bg-violet-100 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {busy ? "Scanning…" : "What am I forgetting?"}
      </button>
      {result && (
        <div className="mt-3 border border-violet-300 bg-violet-50/50" data-testid="forgetting-scan-result">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-violet-200">
            <Sparkles className="w-4 h-4 text-violet-700" />
            <span className="text-sm font-700 text-violet-900">
              {result.ok ? "Nothing critical missing" : "A few things to sort out"}
            </span>
            <button onClick={() => setResult(null)} className="ml-auto" data-testid="forgetting-scan-close" aria-label="Close">
              <X className="w-4 h-4 text-violet-400 hover:text-violet-700" />
            </button>
          </div>
          {result.ai_summary && (
            <p className="px-4 py-2.5 text-sm text-violet-900 border-b border-violet-200" data-testid="forgetting-scan-ai">
              {result.ai_summary}
            </p>
          )}
          <ul className="px-4 py-2.5 space-y-1.5">
            {result.flags.length === 0 && (
              <li className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> Boarding passes, references, reminders and checklist all look covered.
              </li>
            )}
            {result.flags.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid={`forgetting-flag-${i}`}>
                {f.level === "warn"
                  ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                  : <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />}
                <span className="text-zinc-800">
                  {f.text}{" "}
                  {f.link && (f.link.startsWith("#")
                    ? <button onClick={() => document.getElementById(f.link.slice(1))?.scrollIntoView({ behavior: "smooth" })} className="text-sky-700 font-700 hover:underline">Open section</button>
                    : <a href={f.link} className="text-sky-700 font-700 hover:underline">Open</a>)}
                </span>
              </li>
            ))}
          </ul>
          <p className="px-4 pb-2 text-[10px] text-violet-500">Advisory only — always verify entry requirements with official sources.</p>
        </div>
      )}
    </>
  );
}
